import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useSignIn, useAuth } from '@clerk/clerk-react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { track } from '@/lib/analytics';
import { FiChevronLeft } from 'react-icons/fi';
import { Button, Card, Input } from '@/components';
import { isClerkStrategyNotAllowedError } from '@/lib/clerkProEmail';

function proCallbackUrl(): string {
  if (typeof window === 'undefined') return '/pro-callback';
  return `${window.location.origin}/pro-callback`;
}

type Phase = 'email' | 'link_sent' | 'code_entry';

function factorWithEmailId(
  factors: Array<{ strategy: string }> | null | undefined,
  strategy: 'email_link' | 'email_code'
): { emailAddressId: string } | undefined {
  const f = factors?.find(
    (x): x is typeof x & { emailAddressId: string } =>
      x.strategy === strategy && 'emailAddressId' in x
  );
  return f;
}

function SignInProPage() {
  const { signIn, setActive, isLoaded } = useSignIn();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    track('Pro Sign In Screen');
  }, []);

  useEffect(() => {
    if (authLoaded && isSignedIn) {
      navigate({ to: '/pro' });
    }
  }, [authLoaded, isSignedIn, navigate]);

  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<Phase>('email');
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [codeEmailId, setCodeEmailId] = useState<string | null>(null);

  const cancelLinkFlowRef = useRef<(() => void) | null>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (phase === 'code_entry') {
      const t = setTimeout(() => codeInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [phase]);

  useEffect(() => {
    return () => {
      cancelLinkFlowRef.current?.();
    };
  }, []);

  const startSignIn = useCallback(async () => {
    if (!isLoaded || !signIn) return;

    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const result = await signIn.create({ identifier: trimmed });
      const factors = result.supportedFirstFactors;

      const linkF = factorWithEmailId(factors, 'email_link');
      const codeF = factorWithEmailId(factors, 'email_code');

      setCodeEmailId(codeF?.emailAddressId ?? null);

      if (linkF) {
        try {
          const { startEmailLinkFlow, cancelEmailLinkFlow } =
            signIn.createEmailLinkFlow();
          cancelLinkFlowRef.current = cancelEmailLinkFlow;

          setPhase('link_sent');
          setIsSubmitting(false);

          const completed = await startEmailLinkFlow({
            emailAddressId: linkF.emailAddressId,
            redirectUrl: proCallbackUrl(),
          });

          if (completed.status === 'complete' && completed.createdSessionId) {
            await setActive({ session: completed.createdSessionId });
          } else {
            const verStatus = completed.firstFactorVerification?.status;
            if (verStatus === 'expired') {
              setError('The email link has expired. Please try again.');
              setPhase('email');
            } else {
              setError(
                `Sign-in incomplete (${verStatus ?? completed.status ?? 'unknown'}). Please try again.`
              );
              setPhase('email');
            }
          }
          return;
        } catch (linkErr: unknown) {
          if (!isClerkStrategyNotAllowedError(linkErr) || !codeF) {
            throw linkErr;
          }
        }
      }

      if (codeF) {
        await signIn.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: codeF.emailAddressId,
        });
        setPhase('code_entry');
        return;
      }

      setError('Email sign-in is not available for this account.');
    } catch (err: unknown) {
      console.error('[ProSignIn] Error:', err);
      const clerkError = err as {
        errors?: { code?: string; message?: string; longMessage?: string }[];
        message?: string;
      };
      const e0 = clerkError?.errors?.[0];
      if (e0?.code === 'form_identifier_not_found') {
        setError(
          "We couldn't find an account for that email. Use the exact address you signed up with (including +tags), or create an account."
        );
        return;
      }
      const message =
        e0?.longMessage ??
        e0?.message ??
        clerkError?.message ??
        "Couldn't send the sign-in link. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [email, isLoaded, signIn, setActive]);

  const switchToCode = useCallback(async () => {
    if (!isLoaded || !signIn || !codeEmailId) return;
    cancelLinkFlowRef.current?.();
    cancelLinkFlowRef.current = null;
    setIsSubmitting(true);
    setError('');
    try {
      await signIn.prepareFirstFactor({
        strategy: 'email_code',
        emailAddressId: codeEmailId,
      });
      setPhase('code_entry');
    } catch (err: unknown) {
      console.error('[ProSignIn] switchToCode error:', err);
      setError('Could not send code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [isLoaded, signIn, codeEmailId]);

  const verifyCode = useCallback(async () => {
    if (!isLoaded || !signIn) return;
    if (!code.trim()) {
      setError('Please enter the code from your email.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const attempt = await signIn.attemptFirstFactor({
        strategy: 'email_code',
        code: code.trim(),
      });

      if (attempt.status === 'complete' && attempt.createdSessionId) {
        await setActive({ session: attempt.createdSessionId });
      } else {
        setError(`Sign in incomplete. Status: ${attempt.status ?? 'unknown'}`);
      }
    } catch (err: unknown) {
      console.error('[ProSignIn] Verify:', err);
      const clerkError = err as {
        errors?: { message?: string }[];
        message?: string;
      };
      const message =
        clerkError?.errors?.[0]?.message ??
        clerkError?.message ??
        "That code didn't work. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [code, isLoaded, signIn, setActive]);

  useEffect(() => {
    if (
      phase === 'code_entry' &&
      code.length === 6 &&
      !isSubmitting &&
      isLoaded
    ) {
      verifyCode();
    }
  }, [code, phase, isSubmitting, isLoaded, verifyCode]);

  const resetFlow = () => {
    cancelLinkFlowRef.current?.();
    cancelLinkFlowRef.current = null;
    setPhase('email');
    setCode('');
    setError('');
    setCodeEmailId(null);
  };

  const errLower = error.toLowerCase();
  const isAccountNotFound =
    errLower.includes('couldn') && errLower.includes('find') && errLower.includes('account');

  return (
    <div className='min-h-screen bg-sky-light'>
      <div className='max-w-md mx-auto px-6 py-8'>
        <Link
          to='/'
          search={{ hero: undefined }}
          className='inline-flex items-center text-gray-900 hover:text-primary mb-6 -ml-2'
        >
          <FiChevronLeft size={24} />
        </Link>

        <div className='text-center mb-8'>
          <h1
            className='text-3xl font-semibold text-gray-900 mb-3'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            {phase === 'email'
              ? 'Professional sign in'
              : phase === 'link_sent'
                ? 'Check your email'
                : 'Enter your code'}
          </h1>
          <p
            className='text-lg text-gray-500'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            {phase === 'email'
              ? 'Sign in with your work email. We will send a secure link.'
              : phase === 'link_sent'
                ? 'We sent a sign-in link to your email. Click it to continue.'
                : 'We sent a 6-digit code to your email.'}
          </p>
        </div>

        <Card>
          {phase === 'email' ? (
            <>
              <Input
                label='Email'
                type='email'
                autoComplete='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                autoFocus
              />

              {error && (
                <div className='mt-3'>
                  <p className='text-sm text-red-500 text-center'>{error}</p>
                  {isAccountNotFound && (
                    <div className='mt-2 text-center'>
                      <Link
                        to='/auth/pro/sign-up'
                        className='text-primary font-semibold hover:underline'
                      >
                        Create an account
                      </Link>
                    </div>
                  )}
                </div>
              )}

              <div className='mt-4'>
                <Button
                  fullWidth
                  disabled={!isLoaded || isSubmitting}
                  loading={isSubmitting}
                  onClick={startSignIn}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : phase === 'link_sent' ? (
            <div className='text-center text-gray-600 text-sm'>
              <p className='mb-1'>
                Open the email at <span className='font-semibold'>{email}</span>{' '}
                and click the link.
              </p>
              <p className='mb-4 text-xs text-gray-400'>
                This page will update automatically once you do.
              </p>

              {error && (
                <p className='mb-4 text-sm text-red-500'>{error}</p>
              )}

              {codeEmailId && (
                <Button
                  variant='secondary'
                  fullWidth
                  disabled={isSubmitting}
                  onClick={switchToCode}
                >
                  Send me a code instead
                </Button>
              )}

              <div className='mt-3'>
                <Button
                  variant='secondary'
                  fullWidth
                  disabled={isSubmitting}
                  onClick={resetFlow}
                >
                  Use a different email
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Input
                ref={codeInputRef}
                label='Code'
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder='123456'
                type='text'
                inputMode='numeric'
                maxLength={6}
                disabled={isSubmitting}
                autoFocus
              />

              {error && (
                <p className='mt-3 text-sm text-red-500 text-center'>{error}</p>
              )}

              <div className='mt-4'>
                <Button
                  fullWidth
                  disabled={!isLoaded || isSubmitting}
                  loading={isSubmitting}
                  onClick={verifyCode}
                >
                  Continue
                </Button>
              </div>

              <div className='mt-3'>
                <Button
                  variant='secondary'
                  fullWidth
                  disabled={isSubmitting}
                  onClick={resetFlow}
                >
                  Use a different email
                </Button>
              </div>
            </>
          )}
        </Card>

        <div className='flex flex-col items-center gap-3 mt-6 text-center'>
          <div className='flex items-center justify-center gap-2 flex-wrap'>
            <span className='text-gray-500'>Need an account?</span>
            <Link
              to='/auth/pro/sign-up'
              className='text-gray-900 font-bold hover:underline'
            >
              Sign up
            </Link>
          </div>
          <Link
            to='/auth/sign-in'
            className='text-sm text-primary font-semibold hover:underline'
          >
            Co-parent? Sign in with phone
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/auth/pro/sign-in')({
  component: SignInProPage,
});
